/**
 * EDIT_TEMPLATE_SUBTASK.JSX - Subtask/Item Template Editor
 *
 * SUMMARY:
 * Renders subtask (item) templates within the Edit Template modal, including:
 * - Description input with validation
 * - Material, labor, and subcontractor sections
 * - Per-subtask totals row for Material / Contractor / Resource / Planned Effort / Total
 *
 * PROPS:
 * @param {boolean} isSubTask - Indicates item template mode
 * @param {Array} subTaskArray - List of subtask template rows
 * @param {number} profitMargin - Profit margin value
 * @param {string} profitMarginType - Profit margin type (e.g. PERCENTAGE)
 * @param {boolean} isDisabled - Read-only mode flag
 * @param {Function} handleSaveTemplateDialog - Opens save template dialog
 * @param {Function} deleteSubTaskHandler - Handles deletion of a subtask template
 * @param {Function} getDeleteSubTask - Returns delete dialog state
 * @param {Array} categoryList - Category options
 * @param {Array} unitList - Unit options
 * @param {Function} onRefreshTemplateList - Refreshes template list after changes
 */
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import idx from 'idx';
import _ from "lodash";
import { useEffect, useState } from "react";
import Collapse from 'react-bootstrap/Collapse';
import Table from 'react-bootstrap/Table';
import { useDispatch, useSelector } from 'react-redux';
import TextAreaInput from "../../../components/inputs/textarea-input";
import { updateSubTaskAction } from "../../../slice/add-task";
import { subTaskCalculation } from "../../../util/calculations/calculate-sub-task";
import { numberFormat } from "../../../util/currency-formatter";
import { ValidateField, ValidatorTypes } from "../../../util/validators";
import '../../projects/scss/task.scss';
import Labor from "./labor";
import Material from "./material";
import Subcontractor from "./subcontractor";
//import { updateLaborSubtask } from "./binders/labor";
//import { updateMaterialSubTask } from "./binders/material";
import { TextField } from "@material-ui/core";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import Autocomplete from "@mui/material/Autocomplete";
import { listTemplateCategories } from "../../../actions/inventory.action";
import DeleteDialog from "../../../components/delete-dialog";
import TemplateCategory from '../category.jsx';

export default function SubTask({ isSubTask, subTaskArray = [], profitMargin, profitMarginType, isDisabled, handleSaveTemplateDialog, deleteSubTaskHandler, getDeleteSubTask, categoryList, unitList, onRefreshTemplateList, ...props }) {

    const dispatch = useDispatch()
    const [categoryName, setCategoryName] = useState('');
    const [expandSubTask, setExpandSubTask] = useState([])
    const templateCategoryList = useSelector((state) => state.category.categories) || [];
    const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
    const [currentSubTaskIndex, setCurrentSubTaskIndex] = useState(null);
    const [descriptionFields, setDescriptionFields] = useState({
        name: 'description',
        label: 'Description',
        value: '',
        disabled: isDisabled,
        isHTClassRequired: false,
        rows: '',
        error: '',
        className: 'm-0',
        validation: [ValidatorTypes.REQUIRED, ValidatorTypes.TEXTAREA]
    });

    useEffect(() => {
        if (templateCategoryList.length === 0) dispatch(listTemplateCategories()).catch(e => console.log(e))
    }, []);

    const inputChangeHandler = (e, index = "") => {
        const { name, value, type } = e.target;

        let _temp = _.cloneDeep(subTaskArray);
        let fieldValue = value;

        if (name === 'description') {
            fieldValue = value;
            const validation = [ValidatorTypes.REQUIRED, ValidatorTypes.TEXTAREA];
            const { valid, error } = ValidateField({ value: fieldValue, validation });

            if (_temp[index]) {
                _temp[index].description = fieldValue;
                _temp[index].descriptionError = error;
                _temp[index].descriptionValid = valid;
            } else {
                _temp[index] = {
                    description: fieldValue,
                    descriptionError: error,
                    descriptionValid: valid,
                };
            }
        } else if (type === "checkbox") {
            _temp[index]["is_actual_materialcost"] = e.target.checked;
        } else {
            _temp[index][name] = value;
        }
        dispatch(updateSubTaskAction(_temp));
    };
    const { id, open, index, type, currentId, description } = getDeleteSubTask();
    const removeSubtask = () => {
        let _temp = _.cloneDeep(subTaskArray)
        _temp.splice(index, 1)
        dispatch(updateSubTaskAction(_temp))
    }
    // const refreshHandler = (items) => {
    //     let templateList = _.cloneDeep(itemsTemplate);
    //     let _temp = _.cloneDeep(subTaskArray);

    //     const updatedSubtask = _temp.map(subTask => {
    //         const itemToUpdate = items.find(item => (
    //             item.templateId === subTask.templateId &&
    //             item.id === subTask.id &&
    //             item.description === subTask.description
    //         ));

    //         if (itemToUpdate) {
    //             const template = templateList.find(temp => temp.id === (itemToUpdate.templateId || itemToUpdate.templateId));
    //             if (template) {
    //                 const templateMaterials = template.material.map(JSON.parse);
    //                 const templateLabors = template.labor.map(JSON.parse);

    //                 subTask.labor = updateLaborSubtask(subTask, templateLabors, itemToUpdate)

    //                 subTask.material = updateMaterialSubTask(subTask, templateMaterials, itemToUpdate)
    //             }

    //             subTask.templateQuantity = itemToUpdate.templateQuantity;
    //             return subTask;
    //         } else return subTask;
    //     });

    //     dispatch(updateSubTaskAction(updatedSubtask));
    // };

    useEffect(() => {
        setDescriptionFields(
            subTaskArray.map(subtask => ({
                name: 'description',
                label: 'Description',
                value: subtask.description || '',
                disabled: isDisabled,
                isHTClassRequired: false,
                rows: subtask.descriptionError ? 2 : 3,
                error: subtask.descriptionError || '',
                className: 'm-0',
                validation: [ValidatorTypes.REQUIRED, ValidatorTypes.TEXTAREA]
            }))
        );
    }, [subTaskArray, isDisabled]);

    const deleteHandler = async (id) => {
        removeSubtask()
        deleteSubTaskHandler({ subtaskIndex: index, subTaskId: id, description: description });
    };

    const updateDeleteSubTask = () => {
        const subTaskId = id.filter(id => id !== currentId);
        deleteSubTaskHandler({ subTaskId, description: description });
    }

    const getUniqSubTaskId = (oldIds, newId) => {
        return [...new Set([...oldIds, newId])];
    }

    const addSubTaskExpandIndex = (subtaskIndex) => {
        setExpandSubTask((prev) =>
            prev.includes(subtaskIndex) ? prev : [...prev, subtaskIndex]
        )
    }

    const removeSubTaskExpandIndex = (subtaskIndex) => {
        setExpandSubTask((prev) =>
            prev.filter((index) => index !== subtaskIndex)
        )
    }

    return (
        <>
            {openCategoryDialog && (
                <TemplateCategory
                    open={openCategoryDialog}
                    showList={false}
                    type={'subtask'}
                    defaultCategoryName={categoryName}
                    dialogClose={(savedCategory) => {
                        setOpenCategoryDialog(false);
                        if (savedCategory && currentSubTaskIndex !== null) {
                            const newCategory = savedCategory;
                            if (newCategory) {
                                const _temp = _.cloneDeep(subTaskArray);
                                _temp[currentSubTaskIndex].categoryId = newCategory.id;
                                dispatch(updateSubTaskAction(_temp));
                            } else {
                                console.error("No matching category found in the list.");
                            }
                        }
                        setCurrentSubTaskIndex(null);
                    }}
                />
            )}
            {open && (<DeleteDialog
                open={open}
                id={id}
                deleteHandler={() => deleteHandler(id)}
                dialogClose={() => updateDeleteSubTask()}
                moduleName={type}
                description={`${type} ${description}`}
            />)}
            {subTaskArray.map((subtask, subtaskIndex) => {
                let _total = subTaskCalculation({
                    subtask: subtask,
                    profitMargin: profitMargin,
                    profitMarginType: profitMarginType,
                    isForm: true
                })

                const processedMaterialList = (Array.isArray(subtask.material)
                    ? subtask.material.map((materialEntry) => {
                        let material = typeof materialEntry === "string" ? JSON.parse(materialEntry) : materialEntry;

                        if (!material?.id) return material;

                        const isMoved = (subtask.movedMaterials || []).some(movedItem =>
                            String(movedItem.id).trim() === String(material.id).trim()
                        );

                        const isHidden = (subtask.hiddenMaterials || []).some(hiddenItem =>
                            String(hiddenItem.id).trim() === String(material.id).trim()
                        );
                        const isDifferentAccount = (subtask.differentAccountMaterials || []).some(differentAccountItem =>
                            String(differentAccountItem.id).trim() === String(material.id).trim()
                        );

                        const isDuplicate = (subtask.duplicateMaterials || []).some(duplicateItem =>
                            String(duplicateItem.id).trim() === String(material.id).trim()
                        );

                        return {
                            ...material,
                            catalog_moved: isMoved || material.catalog_moved === true,
                            different_account: isDifferentAccount || material.different_account === true,
                            hide_search: isHidden || material.hide_search === true,
                            duplicateStatus: isDuplicate || material.duplicateStatus === true,
                            status:
                                (subtask.inactiveMaterials || []).some(inactive => inactive.id === material.id)
                                    ? 'INACTIVE'
                                    : (material.status || 'ACTIVE'),
                        };
                    }).filter(Boolean)
                    : []);
                const isExpanded = expandSubTask.includes(subtaskIndex);
                return (
                    <div key={subtaskIndex} className="border-primary-app create-table-block px-0 mb-2">
                        <div className={`row g-0 align-items-start ${isExpanded ? "template-inline-summary" : ""}`}>
                        <Collapse in={isExpanded}>
                            <div className="col-lg-4 col-md-12 mb-3 mb-lg-0">
                                <div className="form-group px-3">
                                    <div className="input-block">
                                        <TextAreaInput
                                            {...descriptionFields[subtaskIndex]}
                                            onChange={(e) => {
                                                inputChangeHandler(e, subtaskIndex);
                                                setDescriptionFields(prevDescriptionFields => {
                                                    const updatedFields = [...prevDescriptionFields];
                                                    updatedFields[subtaskIndex] = {
                                                        ...updatedFields[subtaskIndex],
                                                        value: e.target.value,
                                                        rows: e.target.value && e.target.value.trim().length >= 2 ? 3 : 2
                                                    };
                                                    return updatedFields;
                                                });
                                            }}
                                        />
                                    </div>
                                    <div className="category-save-dropdown mb-4">
                                        <Autocomplete
                                            freeSolo
                                            value={templateCategoryList.find(cat => cat.value === subtask.categoryId) || null}
                                            onChange={(event, newValue) => {
                                                const _temp = _.cloneDeep(subTaskArray);
                                                _temp[subtaskIndex].categoryId = newValue ? newValue.value : null;
                                                dispatch(updateSubTaskAction(_temp));
                                            }}
                                            selectOnFocus
                                            handleHomeEndKeys
                                            clearOnEscape={false}
                                            clearOnBlur={false}
                                            disablePortal={true}
                                            id="combo-box-demo"
                                            disabled={isDisabled}
                                            options={templateCategoryList}
                                            onInputChange={(event, newValue) => setCategoryName(newValue)}
                                            getOptionLabel={(option) => option.key}
                                            renderOption={(props, option) => {
                                                if (option.isAddOption) {
                                                    return (
                                                        <li className="fs-6 fw-bold text-center sync-icon m-0 py-1 px-0"
                                                            {...props}
                                                            key="add-option"
                                                            onClick={() => {
                                                                setCurrentSubTaskIndex(subtaskIndex);
                                                                setOpenCategoryDialog(true);
                                                            }}>
                                                            {categoryName !== '' ? `Add "${categoryName}"` : "Add Category"}
                                                        </li>
                                                    );
                                                }
                                                return <li {...props} key={option.value}>{option.key}</li>;
                                            }}
                                            filterOptions={(options, state) => {
                                                const filtered = options.filter((option) =>
                                                    option.key.toLowerCase().includes(state.inputValue.toLowerCase())
                                                );
                                                return [
                                                    { key: 'Add option', value: 'add-option', isAddOption: true }, // Special "Add option" object
                                                    ...filtered
                                                ];
                                            }}

                                            noOptionsText={
                                                <p className="fs-6 fw-bold text-center sync-icon m-0 py-1 px-0" onClick={() => {
                                                    setCurrentSubTaskIndex(subtaskIndex);
                                                    setOpenCategoryDialog(true);
                                                }}>
                                                    {categoryName !== '' ? `Add "${categoryName}"` : "Add Category"}
                                                </p>
                                            }
                                            renderInput={(params) => (
                                                <TextField {...params} label="Category" variant="standard" />
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>

                        </Collapse>
                        <div className={`${isExpanded ? "col-lg-8 col-md-12" : "col-12"} template-table-card table-responsive`}>
                            <Table bordered className="table-create tbl-fixed">
                                <thead>
                                    <tr>
                                        <th style={{ width: isExpanded ? "25%" : "33.333%" }}></th>
                                        {!isExpanded && <th style={{ width: "16.666%" }}></th>}
                                        <th style={{ width: isExpanded ? "12.5%" : "8.333%" }}>Material</th>
                                        <th style={{ width: isExpanded ? "12.5%" : "8.333%" }}>Contractor</th>
                                        <th style={{ width: isExpanded ? "12.5%" : "8.333%" }}>Resource</th>
                                        <th style={{ width: isExpanded ? "12.5%" : "8.333%" }}>Planned Effort</th>
                                        <th style={{ width: isExpanded ? "12.5%" : "8.333%" }}>Total</th>
                                        <th style={{ width: isExpanded ? "12.5%" : "8.333%" }} className="save-icon" onClick={() => {
                                            if (handleSaveTemplateDialog) {
                                                handleSaveTemplateDialog('subtask', subtask);
                                            }
                                        }
                                        }>{handleSaveTemplateDialog && <SaveIcon />}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>
                                            {!isExpanded && (
                                                <>
                                                    <span className="tableinfo text-left">
                                                        {subtask.description}
                                                    </span>
                                                    {subtask.descriptionError && !isDisabled && <span className="text-danger error-text text">
                                                        Please fill Item description with atleast 2 characters
                                                    </span>}
                                                </>
                                            )}
                                        </td>
                                        {!isExpanded && <td></td>}
                                        <td>
                                            <span className="tableinfo">
                                                {numberFormat(idx(_total, _ => _.material.sale) || 0)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="tableinfo">
                                                {numberFormat(idx(_total, _ => _.subcontractor.sale) || 0)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="tableinfo">
                                                {numberFormat(idx(_total, _ => _.labor.sale) || 0)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="tableinfo">
                                                {idx(_total, _ => _.forecastHours) || 0} hrs
                                            </span>
                                        </td>
                                        <td>
                                            <span className="tableinfo">
                                                {numberFormat(idx(_total, _ => _.total) || 0)}
                                            </span>
                                        </td>
                                        <td>
                                            {!isExpanded && <KeyboardArrowDown
                                                onClick={() => addSubTaskExpandIndex(subtaskIndex)}
                                                aria-controls="example-collapse-text"
                                                aria-expanded={isExpanded}
                                            />}
                                            {isExpanded && <KeyboardArrowUp
                                                onClick={() => removeSubTaskExpandIndex(subtaskIndex)}
                                                aria-controls="example-collapse-text"
                                                aria-expanded={isExpanded}
                                            />}

                                            {!isDisabled && <DeleteIcon title="Delete Item Template" className="project-delete-icon"
                                                onClick={() => deleteSubTaskHandler({
                                                    subtaskIndex,
                                                    subTaskId: subtask.id ? getUniqSubTaskId(id, subtask.id) : [],
                                                    type: "Item",
                                                    currentId: subtask.id,
                                                    description: subtask.description
                                                })}
                                                sx={{ fill: '#f66 !important' }}
                                            />}
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </div>
                        </div>
                        <Collapse in={expandSubTask.includes(subtaskIndex)}>
                            <div className="col-12">
                                <div className="px-3">
                                    <div className="material-list-block mb-3">
                                        <Material
                                            material={processedMaterialList}
                                            subTaskArray={subTaskArray}
                                            subTaskArrayIndex={subtaskIndex}
                                            profitMargin={profitMargin}
                                            profitMarginType={profitMarginType}
                                            total={_total}
                                            isDisabled={isDisabled}
                                            categoryList={categoryList}
                                            unitList={unitList}
                                            showReplaceButton={true}
                                            onRefreshTemplateList={onRefreshTemplateList}
                                        />


                                    </div>
                                    <div className="material-list-block mb-3">
                                        <Labor
                                            labor={subtask.labor || []}
                                            laborOptions={props.laborOptions}
                                            laborsList={props.laborOptions}
                                            subTaskArray={subTaskArray}
                                            subTaskArrayIndex={subtaskIndex}
                                            total={_total}
                                            isDisabled={isDisabled}
                                        />
                                    </div>
                                    <div className="material-list-block mb-3">
                                        <Subcontractor
                                            subcontractor={subtask.subcontractor || []}
                                            subcontractorOptions={props.subcontractors || []}
                                            profitMargin={profitMargin}
                                            profitMarginType={profitMarginType}
                                            subTaskArray={subTaskArray}
                                            subTaskArrayIndex={subtaskIndex}
                                            total={_total}
                                            isDisabled={isDisabled}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Collapse>
                    </div>
                )
            })}
        </>

    );

}
